// Building Constants
//
// Single source of truth for the two building enums the portal renders.
// Both maps are pinned value-by-value against the server; see
// src/constants/__tests__/buildings.test.js.

// Building module types.
// Mirrors Reziland.Framework/Enums/BuildingModuleType.cs.
// Display strings are copied verbatim from EnumsController.GetModuleDisplayName so this map and a
// /Enums/module-types response name the same module the same way.
//
// Note this covers all ten modules, including the four system ones (Manager, Staffs, JoinRequests,
// Members). /Enums/module-types deliberately returns only the six licensable modules, but a
// building's own module list can contain any of the ten, so it needs the full map.
export const BuildingModuleNames = {
  0: 'Building Management',
  1: 'Announcements',
  2: 'Amenity Booking',
  3: 'Parking Management',
  4: 'Delivery Tracking',
  5: 'Staff Management',
  6: 'Join Requests',
  7: 'Member Management',
  8: 'Maintenance Requests',
  9: 'Building Assistant',
};

// Building registration types.
// Mirrors Reziland.Framework/Enums/RegisterTypeEnum.cs.
// Wording matches the mobile app's own joinMethods strings so the app and the portal agree.
export const RegisterTypeNames = {
  0: 'Password',
  1: 'Access Code',
  2: 'Request Based',
  3: 'Open Access',
};

// Badge variants for each registration type. Must be variants Badge actually supports.
export const RegisterTypeVariants = {
  0: 'secondary', // Password
  1: 'info',      // Access Code
  2: 'warning',   // Request Based
  3: 'success',   // Open Access
};

/**
 * Get a building module's display name.
 * @param {number} module - BuildingModuleType value
 * @param {Array} [moduleTypes] - Optional API enum data ({ value, name, displayName }), used first
 * @returns {string} - Display name, or `Module {n}` for a value neither source knows
 */
export const getBuildingModuleName = (module, moduleTypes = null) => {
  if (moduleTypes && Array.isArray(moduleTypes)) {
    const item = moduleTypes.find((e) => e.value === module);
    if (item) return item.displayName || item.name;
  }
  return BuildingModuleNames[module] ?? `Module ${module}`;
};

/**
 * Get a registration type's display name.
 * @param {number} registerType - RegisterTypeEnum value
 * @returns {string}
 */
export const getRegisterTypeName = (registerType) => {
  return RegisterTypeNames[registerType] ?? 'Unknown';
};

/**
 * Get the Badge variant for a registration type.
 * @param {number} registerType - RegisterTypeEnum value
 * @returns {string}
 */
export const getRegisterTypeVariant = (registerType) => {
  return RegisterTypeVariants[registerType] ?? 'default';
};

/**
 * Registration type options for a select input, in server enum order.
 * @returns {Array} - Array of { value, label }
 */
export const getRegisterTypeOptions = () =>
  Object.entries(RegisterTypeNames).map(([value, label]) => ({
    value: Number(value),
    label,
  }));
